import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { LogIn, Sparkles, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fn = mode === "login" ? login(email, password) : register(name, email, password);
    const res = await fn;
    setBusy(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Hoş geldin!" : "Hesap oluşturuldu");
      nav("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-9 w-9 rounded-lg bg-foreground flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-background" />
            </div>
            <div>
              <div className="text-lg font-display font-semibold tracking-tight">SwayDesign</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Tasarımcı Paneli</div>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl font-display font-semibold tracking-tight mb-2">
            {mode === "login" ? "Tekrar hoş geldin." : "Hesap oluştur."}
          </h1>
          <p className="text-muted-foreground mb-10">
            {mode === "login" ? "Müşteri kayıtlarına ve dashboard'a erişmek için giriş yap." : "Ücretsiz başla, müşterilerini yönetmeye başla."}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Adın</Label>
                <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Adınız Soyadınız" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" type="email" data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="ornek@mail.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input id="password" type="password" data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="En az 6 karakter" />
            </div>

            <Button type="submit" disabled={busy} data-testid="auth-submit-btn" className="w-full rounded-full h-11 group">
              {busy ? "Bekleniyor..." : mode === "login" ? "Giriş Yap" : "Kaydol"}
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </form>

          <div className="mt-8 text-sm text-muted-foreground">
            {mode === "login" ? (
              <>Hesabın yok mu? <button data-testid="auth-toggle-register" className="text-foreground font-medium underline underline-offset-4" onClick={() => setMode("register")}>Kaydol</button></>
            ) : (
              <>Zaten hesabın var mı? <button data-testid="auth-toggle-login" className="text-foreground font-medium underline underline-offset-4" onClick={() => setMode("login")}>Giriş yap</button></>
            )}
          </div>
        </div>
      </div>

      {/* Right: art */}
      <div className="hidden lg:block relative overflow-hidden bg-neutral-950">
        <img
          src="https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjAzZCUyMGJsZW5kZXJ8ZW58MHx8fHwxNzg1NDEwMTg1fDA&ixlib=rb-4.1.0&q=85"
          alt="art"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/95 via-neutral-950/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-neutral-100">
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-300 mb-4">SwayDesign · v1.0</div>
          <h2 className="text-4xl font-display font-semibold tracking-tight max-w-md leading-tight">
            Twitch, Kick ve ForumTR müşterilerin tek bir panelde.
          </h2>
          <p className="mt-4 text-neutral-300 max-w-md">
            Paketleri, teslimatları, garantileri ve kazançlarını akıllı bir dashboard'da takip et.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            {[
              { k: "Platform", v: "5" },
              { k: "Paket", v: "4" },
              { k: "Durum Rengi", v: "4" },
              { k: "Grafik", v: "∞" },
            ].map((it) => (
              <div key={it.k} className="border border-neutral-800/80 bg-neutral-900/40 backdrop-blur-md rounded-lg p-4">
                <div className="text-2xl font-display font-semibold">{it.v}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-400 mt-1">{it.k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
